import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FlaskConical, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  Users,
  Award,
  AlertTriangle,
  FileText,
  Shield,
  Droplets
} from "lucide-react";

const courseDetails = {
  duration: "8-16 שעות",
  certification: "תעודה מוכרת",
  participants: "עד 20 משתתפים",
  practical: "תרגול מעשי"
};

const hazmatCategories = [
  { category: "חומרים דליקים ונפיצים", examples: "דלקים, גזים, חומרי פיצוץ" },
  { category: "חומרים קורוזיביים", examples: "חומצות, בסיסים, חומרי ניקוי" },
  { category: "חומרים רעילים", examples: "חומרי הדברה, כימיקלים תעשייתיים" },
  { category: "חומרים מסרטנים", examples: "אסבסט, בנזן, פורמלדהיד" },
  { category: "גזים בלחץ", examples: "בלוני גז, מקררים, מערכות פניאומטיות" }
];

const courseContent = [
  {
    title: "זיהוי וסיווג",
    topics: [
      "סיווג UN לחומרים מסוכנים",
      "קריאת תוויות ותמרורים",
      "הבנת גיליונות SDS",
      "זיהוי סימני סכנה"
    ]
  },
  {
    title: "טיפול ואחסון",
    topics: [
      "כללי אחסון נכון",
      "הפרדת חומרים לא תואמים",
      "אוורור ושליטה בטמפרטורה",
      "נהלי מילוי והתרקנות"
    ]
  },
  {
    title: "הגנה אישית",
    topics: [
      "בחירת ציוד מגן מתאים",
      "שימוש במסכות נשימה",
      "כפפות וביגוד מגן",
      "מקלחות ותחנות שטיפה"
    ]
  },
  {
    title: "חירום ודליפות",
    topics: [
      "זיהוי דליפות ושפיכות",
      "ערכות ספיגה וטיפול",
      "פינוי והתרעה",
      "דיווח לרשויות"
    ]
  }
];

const sdsExplanation = [
  { section: "1-3", content: "זיהוי החומר, סיכונים, הרכב" },
  { section: "4-6", content: "עזרה ראשונה, כיבוי אש, שפיכה" },
  { section: "7-10", content: "טיפול, אחסון, יציבות" },
  { section: "11-16", content: "רעילות, אקולוגיה, רגולציה" }
];

const faqs = [
  {
    question: "מי חייב לעבור הדרכת חומרים מסוכנים?",
    answer: "כל עובד שנחשף לחומרים מסוכנים או עובד בקרבתם חייב לעבור הדרכה. זה כולל עובדי ייצור, מחסן, תחזוקה, ניקיון ואבטחה במתקנים עם חומרים מסוכנים."
  },
  {
    question: "מה ההבדל בין קורס 8 שעות ל-16 שעות?",
    answer: "קורס 8 שעות מיועד לעובדים עם חשיפה מוגבלת, וכולל יסודות בלבד. קורס 16 שעות מיועד לעובדים בסיכון גבוה וכולל תרגול מעשי מורחב."
  },
  {
    question: "האם יש תרגול מעשי עם חומרים אמיתיים?",
    answer: "התרגול מתבצע עם חומרי הדמיה בטוחים ולא עם חומרים מסוכנים אמיתיים. התרגול כולל לבישת ציוד מגן, שימוש בערכות ספיגה ותרגילי פינוי."
  },
  {
    question: "כמה זמן התעודה בתוקף?",
    answer: "התעודה תקפה לשנתיים. יש לעבור הדרכת רענון לפני תום התוקף, או מוקדם יותר אם חלים שינויים בסוגי החומרים או בתהליכי העבודה."
  }
];

export default function Hazmat() {
  return (
    <Layout>
      <Helmet>
        <title>הדרכת חומרים מסוכנים | Adam Safety</title>
        <meta name="description" content="הדרכת חומרים מסוכנים מקצועית - זיהוי, טיפול, אחסון ותגובה לחירום. קריאת SDS, ציוד מגן, נהלי בטיחות. תעודה מוכרת." />
        <link rel="canonical" href="https://adam-safety.com/training/hazmat" />
        <meta property="og:title" content="הדרכת חומרים מסוכנים | Adam Safety" />
        <meta property="og:description" content="הדרכת חומרים מסוכנים מקצועית - זיהוי, טיפול, אחסון ותגובה לחירום. קריאת SDS, ציוד מגן, נהלי בטיחות. תעודה מוכרת." />
        <meta property="og:url" content="https://adam-safety.com/training/hazmat" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="הדרכת חומרים מסוכנים | Adam Safety" />
        <meta name="twitter:description" content="הדרכת חומרים מסוכנים מקצועית - זיהוי, טיפול, אחסון ותגובה לחירום. קריאת SDS, ציוד מגן, נהלי בטיחות. תעודה מוכרת." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <FlaskConical className="w-5 h-5" />
              <span className="text-sm font-medium">הדרכת בטיחות</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              הדרכת חומרים מסוכנים
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              הדרכה מקיפה לעבודה בטוחה עם חומרים מסוכנים - 
              זיהוי, טיפול, אחסון ותגובה לחירום
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.certification}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.participants}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמן הדרכה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hazmat Categories */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סוגי חומרים מסוכנים
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {hazmatCategories.map((cat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl p-6 border border-yellow-200 dark:border-yellow-800"
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  <h3 className="font-bold text-yellow-800 dark:text-yellow-200">{cat.category}</h3>
                </div>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">{cat.examples}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              תכנית ההדרכה
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {courseContent.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <h3 className="text-lg font-bold text-foreground mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{topic}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SDS Explanation */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              גיליון בטיחות (SDS)
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              לומדים לקרוא ולהבין את 16 הסעיפים של גיליון הבטיחות
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {sdsExplanation.map((sds, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-5 text-primary-foreground text-center"
              >
                <div className="text-xs font-semibold text-accent/70 uppercase tracking-widest mb-1">סעיפים</div>
                <div className="text-3xl font-bold text-accent mb-2" dir="ltr">{sds.section}</div>
                <p className="text-primary-foreground/80 text-sm">{sds.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              עובדים עם חומרים מסוכנים?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              וודאו שהעובדים שלכם יודעים לעבוד בבטיחות
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמנת הדרכה
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  הדרכות נוספות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}